import React, { useEffect, useMemo, useRef, useState } from 'react';

// Charts for the dashboard. Deliberately hand drawn in SVG rather than pulling
// in a charting library, but drawn in real pixels.
//
// The previous version drew into a 100 by 100 viewBox stretched with
// preserveAspectRatio="none". That scales x about six times harder than y, so
// every dot came out as a fat blob and the line as a thick smear. Measuring the
// container and drawing at true size is what keeps a 3px dot 3px wide. It also
// means one tick label per data point is no longer printed on top of itself:
// the axis picks a handful of ticks that fit the width it actually has.

const valueOf = (item) =>
  (item && (item.value ?? item.revenue ?? item.count)) || 0;

const labelOf = (item) =>
  (item && (item.label || item.name || item.period)) || 'Unknown';

const PALETTE = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
                 '#0EA5E9', '#EC4899', '#84CC16'];

// "2026-04-08" reads as "8 Apr" on an axis, and "2026-04" as "Apr 26". Anything
// else (a client name, a week key) is left exactly as it was given.
const shortLabel = (raw) => {
  const text = String(raw ?? '');
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  if (day) {
    const date = new Date(Number(day[1]), Number(day[2]) - 1, Number(day[3]));
    return `${date.getDate()} ${date.toLocaleString('en-GB', { month: 'short' })}`;
  }
  const month = /^(\d{4})-(\d{2})$/.exec(text);
  if (month) {
    const date = new Date(Number(month[1]), Number(month[2]) - 1, 1);
    return `${date.toLocaleString('en-GB', { month: 'short' })} ${month[1].slice(2)}`;
  }
  return text;
};

const fullLabel = (raw) => {
  const text = String(raw ?? '');
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  if (day) {
    const date = new Date(Number(day[1]), Number(day[2]) - 1, Number(day[3]));
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  }
  return shortLabel(text);
};

// Round the top of the scale up to something a person would choose, so the
// gridline labels read 6M, 4M, 2M rather than 5.73M, 3.82M, 1.91M.
const niceCeiling = (value) => {
  if (!(value > 0)) return 1;
  const magnitude = Math.pow(10, Math.floor(Math.log10(value)));
  const scaled = value / magnitude;
  const step = scaled <= 1 ? 1 : scaled <= 2 ? 2 : scaled <= 2.5 ? 2.5 : scaled <= 5 ? 5 : 10;
  return step * magnitude;
};

/** Track the drawn width of an element, so the SVG can be sized in real pixels. */
const useMeasuredWidth = () => {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const update = () => setWidth(node.clientWidth);
    update();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', update);
      return () => window.removeEventListener('resize', update);
    }
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return [ref, width];
};

const Tooltip = ({ x, y, width, lines }) => {
  if (!lines || lines.length === 0) return null;
  // Keep the card inside the chart rather than letting it hang off the edge.
  const cardWidth = 170;
  const left = Math.min(Math.max(x - cardWidth / 2, 4), Math.max(width - cardWidth - 4, 4));
  return (
    <div
      className="pointer-events-none absolute z-10 rounded-lg bg-slate-900 px-3 py-2 text-xs text-white shadow-lg"
      style={{ left, top: Math.max(y - 12, 0), width: cardWidth }}
    >
      {lines.map((line, index) => (
        <div key={index} className={index === 0 ? 'font-semibold' : 'text-slate-300'}>
          {line}
        </div>
      ))}
    </div>
  );
};

const Empty = ({ message }) => (
  <div
    className="flex h-full items-center justify-center text-sm text-slate-400"
    style={{ minHeight: 120 }}
  >
    {message}
  </div>
);

const AnalyticsChart = ({
  type,
  data,
  title,
  height = 300,
  colors = PALETTE,
  // A line of daily revenue does not need a legend naming all forty five days,
  // and a bar chart already writes its label beside every bar. Only the pie
  // genuinely needs one, so that is the only place it is on by default.
  showLegend = null,
  formatValue = (value) => (typeof value === 'number' ? value.toLocaleString() : value),
  emptyMessage = 'No data available',
}) => {
  const rows = Array.isArray(data) ? data : [];
  const [hover, setHover] = useState(null);
  const [wrapRef, wrapWidth] = useMeasuredWidth();

  const legendVisible = showLegend === null ? type === 'pie' : showLegend;

  // ---------------------------------------------------------------- line ----
  const renderLineChart = () => {
    if (rows.length === 0) return <Empty message={emptyMessage} />;

    const padding = { top: 16, right: 16, bottom: 28, left: 58 };
    const width = Math.max(wrapWidth, 240);
    const plotWidth = Math.max(width - padding.left - padding.right, 10);
    const plotHeight = Math.max(height - padding.top - padding.bottom, 10);

    const values = rows.map(valueOf);
    // Revenue is money, so the scale starts at zero: a chart that starts at the
    // lowest value makes a quiet week look like a collapse.
    const top = niceCeiling(Math.max(...values, 0)) || 1;

    const xAt = (index) =>
      rows.length === 1
        ? padding.left + plotWidth / 2
        : padding.left + (index / (rows.length - 1)) * plotWidth;
    const yAt = (value) => padding.top + plotHeight - (value / top) * plotHeight;

    const points = rows.map((row, index) => ({
      x: xAt(index),
      y: yAt(valueOf(row)),
      row,
      index,
    }));

    const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');
    const areaPath = points.length
      ? `${linePath} L${points[points.length - 1].x},${padding.top + plotHeight}` +
        ` L${points[0].x},${padding.top + plotHeight} Z`
      : '';

    const gridValues = [0, 0.25, 0.5, 0.75, 1].map((f) => top * f);

    // One label every N points, so they never collide however long the period.
    const labelWidth = 62;
    const maxTicks = Math.max(Math.floor(plotWidth / labelWidth), 2);
    const step = Math.max(Math.ceil(rows.length / maxTicks), 1);
    const tickIndexes = [];
    for (let index = 0; index < rows.length; index += step) tickIndexes.push(index);
    // The last period is always worth labelling, but stepping can leave a tick
    // right beside it: "Jul 26" and "Aug 26" ran into each other. Where they
    // would collide, the last one wins.
    const lastIndex = rows.length - 1;
    if (tickIndexes[tickIndexes.length - 1] !== lastIndex) {
      if (xAt(lastIndex) - xAt(tickIndexes[tickIndexes.length - 1]) < labelWidth) {
        tickIndexes.pop();
      }
      tickIndexes.push(lastIndex);
    }

    // Dots on every point turn into a solid band once there are many, so they
    // appear only when there is room, plus whichever point is hovered.
    const showDots = rows.length <= 40;

    const onMove = (event) => {
      const box = event.currentTarget.getBoundingClientRect();
      const x = event.clientX - box.left;
      let nearest = points[0];
      points.forEach((p) => {
        if (Math.abs(p.x - x) < Math.abs(nearest.x - x)) nearest = p;
      });
      setHover(nearest);
    };

    const gradientId = `axm-line-${String(colors[0]).replace('#', '')}`;

    return (
      <div className="relative" style={{ height }}>
        <svg
          width="100%"
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          onMouseMove={onMove}
          onMouseLeave={() => setHover(null)}
          role="img"
        >
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={colors[0]} stopOpacity="0.25" />
              <stop offset="100%" stopColor={colors[0]} stopOpacity="0" />
            </linearGradient>
          </defs>

          {gridValues.map((value) => (
            <g key={value}>
              <line
                x1={padding.left}
                x2={padding.left + plotWidth}
                y1={yAt(value)}
                y2={yAt(value)}
                stroke="#E2E8F0"
                strokeWidth="1"
              />
              <text
                x={padding.left - 8}
                y={yAt(value) + 4}
                textAnchor="end"
                fontSize="11"
                fill="#94A3B8"
              >
                {formatValue(value)}
              </text>
            </g>
          ))}

          {areaPath && <path d={areaPath} fill={`url(#${gradientId})`} />}
          <path
            d={linePath}
            fill="none"
            stroke={colors[0]}
            strokeWidth="2"
            strokeLinejoin="round"
            strokeLinecap="round"
          />

          {showDots &&
            points.map((p) => (
              <circle key={p.index} cx={p.x} cy={p.y} r="3" fill="#FFFFFF"
                      stroke={colors[0]} strokeWidth="2" />
            ))}

          {hover && (
            <g>
              <line
                x1={hover.x}
                x2={hover.x}
                y1={padding.top}
                y2={padding.top + plotHeight}
                stroke={colors[0]}
                strokeWidth="1"
                strokeDasharray="3 3"
              />
              <circle cx={hover.x} cy={hover.y} r="5" fill={colors[0]}
                      stroke="#FFFFFF" strokeWidth="2" />
            </g>
          )}

          {tickIndexes.map((index) => (
            <text
              key={index}
              x={xAt(index)}
              y={height - 8}
              textAnchor={index === 0 ? 'start' : index === rows.length - 1 ? 'end' : 'middle'}
              fontSize="11"
              fill="#94A3B8"
            >
              {shortLabel(labelOf(rows[index]))}
            </text>
          ))}
        </svg>

        {hover && (
          <Tooltip
            x={hover.x}
            y={hover.y}
            width={width}
            lines={[
              fullLabel(labelOf(hover.row)),
              formatValue(valueOf(hover.row)),
              hover.row.count !== undefined
                ? `${hover.row.count} ${hover.row.count === 1 ? 'invoice' : 'invoices'}`
                : null,
            ].filter(Boolean)}
          />
        )}
      </div>
    );
  };

  // ----------------------------------------------------------------- bar ----
  const renderBarChart = () => {
    if (rows.length === 0) return <Empty message={emptyMessage} />;

    const maxValue = Math.max(...rows.map(valueOf), 0);

    // Bars size to their content. Reserving the full chart height left a block
    // of white under five rows and made the card look unfinished.
    return (
      <div className="relative space-y-2.5">
        {rows.map((item, index) => {
          const value = valueOf(item);
          const percentage = maxValue > 0 ? (value / maxValue) * 100 : 0;
          const color = colors[index % colors.length];
          const name = labelOf(item);

          return (
            <div
              key={index}
              className="flex items-center gap-3"
              onMouseEnter={(event) => {
                const box = event.currentTarget.getBoundingClientRect();
                const parent = event.currentTarget.parentElement.getBoundingClientRect();
                setHover({ key: index, x: box.width / 2, y: box.top - parent.top, item });
              }}
              onMouseLeave={() => setHover(null)}
            >
              {/* Full name in the title, so a truncated dealer name is still readable */}
              <div className="w-36 shrink-0 truncate text-sm text-slate-600" title={name}>
                {name}
              </div>
              <div className="h-6 flex-1 overflow-hidden rounded bg-slate-100">
                <div
                  className="h-full rounded transition-all duration-500 ease-out"
                  style={{ width: `${percentage}%`, backgroundColor: color }}
                />
              </div>
              <div className="w-24 shrink-0 text-right text-sm font-medium text-slate-900">
                {formatValue(value)}
              </div>
            </div>
          );
        })}

        {hover && hover.item && (
          <Tooltip
            x={hover.x}
            y={hover.y}
            width={wrapWidth}
            lines={[
              labelOf(hover.item),
              formatValue(valueOf(hover.item)),
              hover.item.count !== undefined ? `${hover.item.count} invoices` : null,
            ].filter(Boolean)}
          />
        )}
      </div>
    );
  };

  // ----------------------------------------------------------------- pie ----
  const renderPieChart = () => {
    if (rows.length === 0) return <Empty message={emptyMessage} />;

    const total = rows.reduce((sum, item) => sum + valueOf(item), 0);
    if (total <= 0) return <Empty message={emptyMessage} />;

    const size = 200;
    const centre = size / 2;
    const outer = 78;
    const inner = 48;

    const point = (radius, degrees) => {
      // Start at twelve o'clock, which is where a reader expects a pie to begin.
      const radians = ((degrees - 90) * Math.PI) / 180;
      return [centre + radius * Math.cos(radians), centre + radius * Math.sin(radians)];
    };

    let cursor = 0;
    const slices = rows.map((item, index) => {
      const value = valueOf(item);
      // The old code added percentage * 360 here, with percentage already on a
      // 0 to 100 scale, so every slice swept up to a hundred times too far and
      // the arcs wrapped over each other.
      const sweep = (value / total) * 360;
      const start = cursor;
      const end = cursor + sweep;
      cursor = end;

      const [x1, y1] = point(outer, start);
      const [x2, y2] = point(outer, end);
      const [x3, y3] = point(inner, end);
      const [x4, y4] = point(inner, start);
      const largeArc = sweep > 180 ? 1 : 0;

      // A single category fills the ring; an arc cannot draw 360 degrees in one
      // go because the start and end points land on top of each other.
      const path =
        sweep >= 359.999
          ? `M${centre - outer},${centre} A${outer},${outer} 0 1 1 ${centre + outer},${centre}` +
            ` A${outer},${outer} 0 1 1 ${centre - outer},${centre} Z` +
            `M${centre - inner},${centre} A${inner},${inner} 0 1 0 ${centre + inner},${centre}` +
            ` A${inner},${inner} 0 1 0 ${centre - inner},${centre} Z`
          : `M${x1},${y1} A${outer},${outer} 0 ${largeArc} 1 ${x2},${y2}` +
            ` L${x3},${y3} A${inner},${inner} 0 ${largeArc} 0 ${x4},${y4} Z`;

      return {
        path,
        item,
        index,
        value,
        share: (value / total) * 100,
        color: colors[index % colors.length],
        fillRule: sweep >= 359.999 ? 'evenodd' : 'nonzero',
      };
    });

    const active = hover && hover.key === 'pie' ? hover.slice : null;

    return (
      <div className="flex items-center justify-center" style={{ minHeight: height }}>
        <div className="relative" style={{ width: size, height: size }}>
          <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img">
            {slices.map((slice) => (
              <path
                key={slice.index}
                d={slice.path}
                fill={slice.color}
                fillRule={slice.fillRule}
                opacity={active && active.index !== slice.index ? 0.35 : 1}
                className="cursor-pointer transition-opacity"
                onMouseEnter={() => setHover({ key: 'pie', slice })}
                onMouseLeave={() => setHover(null)}
              />
            ))}
          </svg>

          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
            <div className="text-xl font-bold text-slate-900">
              {active ? formatValue(active.value) : formatValue(total)}
            </div>
            <div className="max-w-[110px] truncate text-xs text-slate-500">
              {active ? `${labelOf(active.item)} ${active.share.toFixed(1)}%` : 'Total'}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // -------------------------------------------------------------- metric ----
  const renderMetricCard = () => {
    if (rows.length === 0) return <Empty message={emptyMessage} />;
    return (
      <div className="grid grid-cols-2 gap-4">
        {rows.map((item, index) => (
          <div key={index} className="rounded-lg bg-slate-50 p-3 text-center">
            <div className="text-2xl font-bold text-slate-900">{formatValue(valueOf(item))}</div>
            <div className="text-sm text-slate-600">{labelOf(item)}</div>
          </div>
        ))}
      </div>
    );
  };

  const renderChart = () => {
    switch (type) {
      case 'line':
        return renderLineChart();
      case 'pie':
        return renderPieChart();
      case 'metric':
        return renderMetricCard();
      case 'bar':
      default:
        return renderBarChart();
    }
  };

  const total = useMemo(() => rows.reduce((sum, item) => sum + valueOf(item), 0), [rows]);

  const renderLegend = () => {
    if (!legendVisible || rows.length === 0) return null;
    return (
      <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2">
        {rows.map((item, index) => (
          <div key={index} className="flex items-center gap-2">
            <span
              className="h-3 w-3 rounded-sm"
              style={{ backgroundColor: colors[index % colors.length] }}
            />
            <span className="text-sm text-slate-600">
              {labelOf(item)}
              {type === 'pie' && total > 0 && (
                <span className="ml-1 text-slate-400">
                  {((valueOf(item) / total) * 100).toFixed(1)}%
                </span>
              )}
            </span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div ref={wrapRef} className="w-full">
      {title && <h3 className="mb-4 text-lg font-semibold text-slate-900">{title}</h3>}
      {renderChart()}
      {renderLegend()}
    </div>
  );
};

export default AnalyticsChart;
